import { QRCodeSVG } from "qrcode.react";
import type { CardCanvasProps } from "./types";
import { DOMAIN_COLORS, DOMAIN_LABELS_AR } from "./constants";
import { getCardBg } from "./styleHelpers";
import CardLogo from "./CardLogo";
import WatermarkLogo from "./WatermarkLogo";
import CardDecorations from "./CardDecorations";
import TopDomainsBadges from "./TopDomainsBadges";
import { InstagramStrengthRow } from "./StrengthRows";

/** Instagram card — Arabic / RTL (1080×1080) */
export default function InstagramCardRTL({
  userName,
  strengths,
  topDomains,
  platformUrl,
  profileUrl,
  cardRef,
  template,
}: CardCanvasProps) {
  const top10 = strengths.slice(0, 10);

  return (
    <div
      ref={cardRef}
      style={{
        width: 1080,
        height: 1080,
        background: getCardBg(template, "instagram"),
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Cairo', 'Noto Sans Arabic', 'Tahoma', sans-serif",
        color: "#fff",
        direction: "rtl",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardDecorations template={template} width={1080} height={1080} />
      <WatermarkLogo />

      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: template === "classic" ? "42px 50px 0" : "36px 50px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
        }}
      >
        <CardLogo size={52} />
        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.5)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 20,
            padding: "6px 18px",
          }}
        >
          {platformUrl.replace(/^https?:\/\//, "")}
        </div>
      </div>

      {/* Title */}
      <div
        style={{
          flexShrink: 0,
          padding: "22px 50px 16px",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", letterSpacing: 2, marginBottom: 8 }}>
          ملف نقاط القوة كليفتون
        </div>
        <div style={{ fontSize: 38, fontWeight: template === "minimal" ? 600 : 800, marginBottom: 6 }}>
          {userName}
        </div>
        <div style={{ fontSize: 15, color: "rgba(255,255,255,0.55)" }}>أفضل 10 نقاط قوة</div>
      </div>

      {/* Top Domains */}
      <div style={{ flexShrink: 0, padding: "0 50px 18px", position: "relative", zIndex: 1 }}>
        <TopDomainsBadges topDomains={topDomains} isRTL={true} />
      </div>

      {/* Strengths list */}
      <div
        style={{
          flex: 1,
          padding: "0 50px",
          display: "flex",
          flexDirection: "column",
          gap: template === "minimal" ? 0 : 9,
          position: "relative",
          zIndex: 1,
        }}
      >
        {top10.map((s) => (
          <InstagramStrengthRow key={s.rank} s={s} template={template} isRTL={true} />
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          flexShrink: 0,
          padding: "14px 50px 34px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: 10,
            borderRadius: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
          }}
        >
          <QRCodeSVG value={profileUrl} size={82} level="M" />
          <div style={{ fontSize: 9, color: "#333", textAlign: "center", fontFamily: "sans-serif" }}>
            استكشف المنصة
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
          {Object.entries(DOMAIN_COLORS).map(([domain, color]) => (
            <div key={domain} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                {DOMAIN_LABELS_AR[domain] ?? domain}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
